import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import {
  FormGroup,
  FormGroupDirective,
  FormControl,
  ControlContainer,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { MbbProduct } from '../../../models/mbb-product.model';
import { MbbAllowedChangeInfo } from '../../../models/mbb-offerings-response.model';
import { distinctUntilChanged, debounceTime, tap, map } from 'rxjs/operators';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { AssignedSubscriptionModel } from '@app/shared/models/assigned-subscription-offering.model';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { SortByPipe } from '@sales/modules/mbb/pipes/sort-by.pipe';
import { IceBadgeComponent } from '@app/shared/components/atoms/ice-badge/ice-badge.component';
import { IceSearchFieldComponent } from '@app/shared/components/molecules/ice-search-field/ice-search-field.component';
import { IceRadioListItemComponent } from '@app/shared/components/atoms/ice-radio-list-item/ice-radio-list-item.component';

@Component({
  selector: 'app-mbb-subscription',
  templateUrl: './mbb-subscription.component.html',
  styleUrls: ['./mbb-subscription.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IceSearchFieldComponent,
    IceRadioListItemComponent,
    IceBadgeComponent,
    SortByPipe,
    PriceFormatPipe,
  ],
})
export class MbbSubscriptionComponent implements OnInit, OnChanges, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);

  PriceType = PriceType;

  @Input() mbbProduct: MbbProduct;
  @Input() assignedSubscription: AssignedSubscriptionModel = null;
  @Input() mbbAllowedOfferings: MbbAllowedChangeInfo[];
  @Output() mbbSubscriptionChange = new EventEmitter();
  @Output() isInvalid = new EventEmitter();

  form: FormGroup;
  searchValue = '';
  allowedMbbOfferingsFiltered: MbbAllowedChangeInfo[] = [];
  subscription$ = new Subscription();

  static buildChooseSubscriptionFormGroup(offering: string) {
    return new FormGroup({ offering: new FormControl(offering, [Validators.required]) });
  }

  ngOnInit() {
    this.form = this.buildForm(this.mbbProduct, this.assignedSubscription?.subscriptionNo.toString());

    this.subscription$ = this.form.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        map(value => this.mbbAllowedOfferings.find(offering => offering.subscriptionOffering.id === value.offering)),
        tap((selectedOffering: MbbAllowedChangeInfo) => {
          if (selectedOffering) {
            this.mbbSubscriptionChange.emit({
              offering: selectedOffering.subscriptionOffering,
              changeInfo: selectedOffering.changeInfo,
            });
          }
        }),
        tap(() => this.isInvalid.emit(this.form.invalid))
      )
      .subscribe();

    this.isInvalid.emit(this.form.invalid);

    this.onFilterChange(this.searchValue);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mbbAllowedOfferings && !changes.mbbAllowedOfferings.firstChange) {
      this.onFilterChange(this.searchValue);
    }
  }

  onFilterChange(value: string) {
    this.searchValue = value;

    if (this.searchValue) {
      this.allowedMbbOfferingsFiltered = this.mbbAllowedOfferings.filter(x =>
        x.subscriptionOffering.name.toLocaleLowerCase().includes(this.searchValue.toLowerCase())
      );
    } else {
      this.allowedMbbOfferingsFiltered = this.mbbAllowedOfferings;
    }
  }

  buildForm(mbbProduct: MbbProduct, existingOfferingId: string): FormGroup {
    this.formGroupDirective.form.addControl(
      'chooseMbbSubscription',
      MbbSubscriptionComponent.buildChooseSubscriptionFormGroup(
        mbbProduct?.mbbSubscription?.id ? mbbProduct.mbbSubscription.id : existingOfferingId
      )
    );

    return this.formGroupDirective.form.get('chooseMbbSubscription') as FormGroup;
  }

  ngOnDestroy() {
    this.subscription$.unsubscribe();
  }
}
